import AppDescription from '@nxsys/components/AppDescription';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import { defaultStatusOptions } from '@nxsys/constants/AppConstants';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { useUpdateAgencyContactById } from '@nxsys/hooks/api/agency';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { UpdateAgencyContactParams } from '@nxsys/types/api';
import { Icons } from '@nxsys/types/icons';
import { AgencyContactType } from '@nxsys/types/models/permissions';
import { formatStatus } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { Descriptions } from 'antd';
import { useRef, useState } from 'react';
import AgencyContactForm, {
  AgencyContactFormRef,
  AgencyContactFormValues,
} from '../components/AgencyContactForm';
import AppTagStatus from '@nxsys/components/AppTagStatus';

type DetailAgencyModalProps = {
  isOpen: boolean;
  toggle: () => void;
  agencyData: IAgency;
  refetchData: () => void;
  data: AgencyContactType | null;
};

const DetailAgencyContactModal: React.FC<DetailAgencyModalProps> = ({
  isOpen,
  toggle,
  agencyData,
  refetchData,
  data: dataProps,
}) => {
  const { messages } = useTypedIntl();
  const agencyId = agencyData.id;

  const [isEditing, setIsEditing] = useState(true);

  const {
    run: updateAgencyContactById,
    loading: isUpdatingAgencyContact,
    value: updateAgencyContactByIdValue,
  } = useUpdateAgencyContactById();
  const updatedData = updateAgencyContactByIdValue?.data;
  const data = updatedData ?? dataProps;

  const formRef = useRef<AgencyContactFormRef>(null);
  const handleSubmit = async (values: AgencyContactFormValues) => {
    if (!agencyId || !data?.id) return;
    const params: UpdateAgencyContactParams = {
      name: values.name,
      email: values.email,
      phone: values.phone,
      status: values.status?.value ?? null,
      companyRef: values.companyRef?.value ?? null,
      department: values.department,
    };
    updateAgencyContactById(data.id, params).then(() => {
      setIsEditing(false);
      refetchData();
      showMessage({
        type: 'success',
        content: messages['common.updateAgencyContactSuccess'],
      });
    });
  };

  return (
    <AppModalContainer
      title={messages['common.siteDetail']}
      isOpen={isOpen}
      toggle={toggle}
      actions={
        !isEditing
          ? [
              {
                name: messages['common.cancel'],
                variant: 'outlined',
                onClick: toggle,
              },
              {
                name: messages['common.edit'],
                onClick: () => setIsEditing(true),
                leftIcon: (
                  <Icons.IconEdit color={defaultTheme.theme.palette.white} />
                ),
              },
            ]
          : [
              {
                name: messages['common.cancel'],
                variant: 'outlined',
                onClick: () => setIsEditing(false),
              },
              {
                name: messages['common.edit'],
                buttonType: 'primary',
                onClick: () => formRef.current?.submit(),
                loading: isUpdatingAgencyContact,
                disabled: isUpdatingAgencyContact,
              },
            ]
      }
    >
      {!isEditing ? (
        <AppDescription
          column={1}
          labelStyle={{ width: 300 }}
          bordered={false}
          colon={false}
          size="middle"
        >
          <Descriptions.Item label={messages['common.name']}>
            {data?.name ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label={messages['common.email']}>
            {data?.email ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label={messages['common.phone']}>
            {data?.phone ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label={messages['common.status']}>
            <AppTagStatus
              className={`tag-popup ${formatStatus(data?.status ?? '')}`}
            >
              {formatStatus(data?.status ?? '')}
            </AppTagStatus>
          </Descriptions.Item>
          <Descriptions.Item label={messages['common.payrollCompany']}>
            {data?.companyRef?.companyName ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label={messages['common.branch']}>
            {data?.department ?? '-'}
          </Descriptions.Item>
        </AppDescription>
      ) : (
        <AgencyContactForm
          formRef={formRef}
          onSubmit={handleSubmit}
          defaultValues={{
            companyRef: data?.companyRef
              ? {
                  label: data?.companyRef?.companyName,
                  value: data?.companyRef?.id,
                }
              : null,
            department: data?.department ?? '',
            email: data?.email ?? '',
            name: data?.name ?? '',
            phone: data?.phone ?? '',
            status: data?.status
              ? {
                  label:
                    defaultStatusOptions.find(
                      (status) => status.value === data?.status,
                    )?.label ?? '',
                  value: data?.status,
                }
              : null,
          }}
        />
      )}
    </AppModalContainer>
  );
};

export default DetailAgencyContactModal;
